/**
 * ComplianceDashboard
 *
 * Dashboard for compliance officers to review pending guardian enrollments.
 */

import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Stack,
  Badge,
  Table,
  ActionIcon,
  Loader,
  Alert,
  Modal,
  Textarea,
  Checkbox,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCheck,
  IconX,
  IconAlertCircle,
  IconEye,
  IconFileText,
} from '@tabler/icons-react';
import {
  useGuardian,
  Guardian,
  getGuardianTypeLabel,
  getGuardianStatusLabel,
  getGuardianStatusColor,
} from './hooks/useGuardian';

export function ComplianceDashboard() {
  const { pendingEnrollments, loading, error, verifyGuardian, clearError } = useGuardian();
  const [selectedGuardian, setSelectedGuardian] = useState<Guardian | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [documentsVerified, setDocumentsVerified] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);

  const handleReview = (guardian: Guardian) => {
    setSelectedGuardian(guardian);
    setReviewNotes('');
    setDocumentsVerified(false);
    open();
  };

  const handleApprove = async () => {
    if (!selectedGuardian) return;

    const success = await verifyGuardian({
      guardianId: selectedGuardian.guardianId,
      userId: selectedGuardian.userId,
      approve: true,
      notes: reviewNotes || undefined,
    });

    if (success) {
      close();
      setSelectedGuardian(null);
    }
  };

  const handleDeny = async () => {
    if (!selectedGuardian) return;

    const success = await verifyGuardian({
      guardianId: selectedGuardian.guardianId,
      userId: selectedGuardian.userId,
      approve: false,
      notes: reviewNotes || 'Denied by compliance officer',
    });

    if (success) {
      close();
      setSelectedGuardian(null);
    }
  };

  const formatDate = (isoString?: string) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString();
  };

  if (loading && pendingEnrollments.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" mb="xl">
        <div>
          <Title order={2}>Compliance Review</Title>
          <Text c="dimmed" size="sm">
            Review and approve pending guardian enrollments
          </Text>
        </div>
        <Badge size="lg" color={pendingEnrollments.length > 0 ? 'yellow' : 'green'}>
          {pendingEnrollments.length} Pending
        </Badge>
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mb="md"
          onClose={clearError}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      {pendingEnrollments.length === 0 ? (
        <Paper p="xl" ta="center" bg="green.0">
          <IconCheck size={48} color="green" />
          <Text mt="md" fw={500}>No pending enrollments</Text>
          <Text size="sm" c="dimmed">All guardian enrollments have been reviewed</Text>
        </Paper>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Guardian Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>For User</Table.Th>
              <Table.Th>Relationship</Table.Th>
              <Table.Th>Documents</Table.Th>
              <Table.Th>Submitted</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pendingEnrollments.map((guardian) => {
              const totalDocs = guardian.documents?.length || 0;

              return (
                <Table.Tr key={guardian.guardianId}>
                  <Table.Td>{guardian.guardianName}</Table.Td>
                  <Table.Td>{getGuardianTypeLabel(guardian.guardianType)}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{guardian.userId}</Text>
                  </Table.Td>
                  <Table.Td>{guardian.relationship}</Table.Td>
                  <Table.Td>
                    <Badge color={totalDocs > 0 ? 'blue' : 'gray'}>
                      {totalDocs} document{totalDocs !== 1 ? 's' : ''}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{formatDate(guardian.createdAt)}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="filled"
                        color="blue"
                        onClick={() => handleReview(guardian)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      {/* Review Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title="Review Guardian Enrollment"
        size="lg"
      >
        {selectedGuardian && (
          <Stack>
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Text fw={500}>Guardian Information</Text>
                <Badge color={getGuardianStatusColor(selectedGuardian.status)}>
                  {getGuardianStatusLabel(selectedGuardian.status)}
                </Badge>
              </Group>

              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Name</Text>
                  <Text size="sm">{selectedGuardian.guardianName}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Type</Text>
                  <Text size="sm">{getGuardianTypeLabel(selectedGuardian.guardianType)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Relationship</Text>
                  <Text size="sm">{selectedGuardian.relationship}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">For User</Text>
                  <Text size="sm">{selectedGuardian.userId}</Text>
                </Group>
              </Stack>
            </Paper>

            {selectedGuardian.documents && selectedGuardian.documents.length > 0 && (
              <Paper p="md" withBorder>
                <Group gap="sm" mb="sm">
                  <IconFileText size={20} />
                  <Text fw={500}>Documents ({selectedGuardian.documents.length})</Text>
                </Group>
                <Stack gap="xs">
                  {selectedGuardian.documents.map((doc, index) => (
                    <Group key={index} justify="space-between">
                      <Text size="sm">{doc.filename}</Text>
                      <Badge size="sm" color={doc.verified ? 'green' : 'yellow'}>
                        {doc.verified ? 'Verified' : 'Pending'}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              </Paper>
            )}

            <Checkbox
              label="I have verified all documents are authentic and valid"
              checked={documentsVerified}
              onChange={(e) => setDocumentsVerified(e.currentTarget.checked)}
            />

            <Textarea
              label="Review Notes"
              placeholder="Add any notes about this review..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              minRows={3}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button
                color="red"
                leftSection={<IconX size={16} />}
                onClick={handleDeny}
              >
                Deny
              </Button>
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={handleApprove}
                disabled={!documentsVerified}
              >
                Approve
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}

export default ComplianceDashboard;
